import { BaseModel } from "../../../service/models/base";
import { MODULE } from "../../../utils/enums";

export interface AdditionalFund extends BaseModel {
  fund_amount?: number;
  updated_at?: Date;
  updated_by?: string;
  updated_by_name?: string;
}

export interface ApprovalModel extends BaseModel {
  level: string;
  approval_status: string;
  approved_at?: Date;
  approved_by?: string;
  approved_by_name?: string;
  reason?: string;
}

export interface CostCenter extends BaseModel {
  name: string;
  description: string;
  fromMonth?: Date;
  toMonth?: Date;
  period: number;
  budget: number;
  addAdditionalBudget?: number;
  duplicateAdditionalBudget?: number;
  totalAdditionalFunds?: number;
  additionalFunds?: AdditionalFund[];
  totalBudget: number;
  lastApprovedBudget: number;
  spent: number;
  balance: number;
  linked_projects_count?: number;
  approvalStatus: string;
  rejectReason?: string;
  costCenterStatus: string;
  remarks: string;
  approved_at?: Date;
  approved_by?: string;
  approved_by_name?: string;
  approval_level?: string;
  approvals?: ApprovalModel[];
  current_approval_level?: string;
  current_approval_required_count?: number;
  current_approval_count?: number;
  current_reject_count?: number;
  // --- New flow fields (Phase 2). Presence of business_unit_id routes the CC through the new approval flow.
  business_unit_id?: string;
  business_unit_name?: string;
  business_unit_code?: string;
  department_id?: string;
  department_name?: string;
  department_code?: string;
  spoc_user_ids?: string[];
  monthly_estimate?: number;
  current_month_status?: string;
  // --- Phase 4
  current_month?: string;
  current_month_approvals?: ApprovalModel[];
  renewal_history?: RenewalHistoryEntry[];
  // --- Phase 7B: in-flight budget-increase requests
  additional_fund_requests?: AdditionalFundRequest[];
}

export interface AdditionalFundRequest extends BaseModel {
  request_id: string;
  requested_amount: number;
  current_amount: number;
  reason?: string;
  approval_level: string;
  approvals?: ApprovalModel[];
  current_approval_level?: string;
  current_approval_required_count?: number;
  current_approval_count?: number;
  current_reject_count?: number;
  approvalStatus: string;
  rejectReason?: string;
  requested_by: string;
  requested_by_name: string;
  requested_at: Date | string;
  approved_at?: Date | string;
  approved_by?: string;
  approved_by_name?: string;
}

export interface RenewalHistoryEntry {
  month: string;
  prior_estimate: number;
  new_estimate: number;
  prior_month_actual: number;
  delta: number;
  approvals: ApprovalModel[];
  approved_at?: Date | string;
  status: string;
}

export const CCMonthStatus = {
  active: "Active",
  pendingRenewal: "Pending Renewal",
  rejected: "Renewal Rejected",
} as const;

export const CostCenterCloseStatus = {
  closed: "Closed",
  open: "Open",
};

export const MasterCostCenterStatus = {
  approved: "Approved",
  rejected: "Rejected",
  // savedDraft: "Saved Draft",
  // updatePending: "Update Pending",
  pendingForApproval: "Pending for Approval",
  level2Pendng: "L2 Pending",
  // --- New flow (Phase 2)
  pendingDeptHead: "Pending Department Head Approval",
};

export const costCenterDefaultValue: CostCenter = {
  name: "",
  description: "",
  period: 0,
  budget: 0,
  totalBudget: 0,
  lastApprovedBudget: 0,
  spent: 0,
  balance: 0,
  approvalStatus: MasterCostCenterStatus.pendingForApproval,
  costCenterStatus: "--",
  remarks: "",
};

export interface CostCenterFilterData {
  id?: string;
  name?: string;
  costCenterStatus?: string[];
  approvalStatus?: string[];
  fromMonth?: Date;
  toMonth?: Date;
  search?: string;
  page: number;
  count: number;
  all?: any;
}

export const getCostCenterApprovedCounts = ({
  approvals = [],
}: {
  approvals?: ApprovalModel[];
}) => {
  let counts: { [key: string]: number } = {};
  for (let i = 0; i < approvals.length; i++) {
    const approval = approvals[i];
    if (approval.approval_status === MasterCostCenterStatus.approved) {
      if (!counts[approval.level]) {
        counts[approval.level] = 0;
      }
      counts[approval.level]++;
    }
  }

  return counts;
};

export const approval_level = {
  level_1: "level_1", //Priya (L1)
  level_2: "level_2", //Akshay (L2)
  // --- New flow (Phase 2)
  bu_head: "bu_head",
  dept_head: "dept_head",
  bu_dept: "bu_dept",
};

export const levelModule = {
  [approval_level.level_1]: MODULE.cost_center_approval_L1,
  [approval_level.level_2]: MODULE.cost_center_approval_L2,
  [approval_level.bu_head]: MODULE.business_unit_head,
  [approval_level.dept_head]: MODULE.department_head,
};

export const approval_threshold: {
  [k: string]: {
    threshold: {
      min: number | null;
      max: number | null;
    };
    approvals_required: {
      level: string;
      count: number;
    }[];
  };
} = {
  [approval_level.level_1]: {
    threshold: {
      min: null,
      max: null,
    },
    approvals_required: [
      {
        level: approval_level.level_1,
        count: 1,
      },
    ],
  },
  [approval_level.level_2]: {
    threshold: {
      min: null,
      max: null,
    },
    approvals_required: [
      {
        level: approval_level.level_1,
        count: 1,
      },
      {
        level: approval_level.level_2,
        count: 1,
      },
    ],
  },
  // --- New flow (Phase 2): BU Head → Department Head
  [approval_level.bu_dept]: {
    threshold: {
      min: null,
      max: null,
    },
    approvals_required: [
      {
        level: approval_level.bu_head,
        count: 1,
      },
      {
        level: approval_level.dept_head,
        count: 1,
      },
    ],
  },
};
